<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { conversationAnalysisAPI } from 'dashboard/api/iaReports';

const today = new Date();
const defaultFrom = new Date(today);
defaultFrom.setDate(today.getDate() - 29);

const fromDate       = ref(defaultFrom.toISOString().slice(0, 10));
const toDate         = ref(today.toISOString().slice(0, 10));
const filterClass    = ref('');
const page           = ref(1);

const loading      = ref(false);
const error        = ref(null);
const reports      = ref([]);
const meta         = ref({ total: 0, pages: 1 });
const summary      = ref(null);
const loadingSum   = ref(false);

const CLASSIFICATION_LABELS = {
  positive: { label: 'Positivo', color: 'text-emerald-700', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200', icon: 'i-lucide-thumbs-up', dot: 'bg-emerald-500' },
  neutral:  { label: 'Neutro',   color: 'text-blue-700',    bg: 'bg-blue-50 dark:bg-blue-900/20',       border: 'border-blue-200',   icon: 'i-lucide-minus',      dot: 'bg-blue-400'  },
  critical: { label: 'Crítico',  color: 'text-red-700',     bg: 'bg-red-50 dark:bg-red-900/20',         border: 'border-red-200',    icon: 'i-lucide-alert-triangle', dot: 'bg-red-500' },
  pending_classification: { label: 'Pendente', color: 'text-n-slate-9', bg: 'bg-n-alpha-2', border: 'border-n-weak', icon: 'i-lucide-clock', dot: 'bg-n-slate-6' },
};

function classInfo(cls) {
  return CLASSIFICATION_LABELS[cls] || CLASSIFICATION_LABELS['neutral'];
}

async function fetchSummary() {
  loadingSum.value = true;
  try {
    const res = await conversationAnalysisAPI.getSummary({ from: fromDate.value, to: toDate.value });
    summary.value = res.data;
  } catch { /* non-critical */ }
  finally { loadingSum.value = false; }
}

async function fetchReports() {
  loading.value = true;
  error.value   = null;
  try {
    const params = { from: fromDate.value, to: toDate.value, page: page.value, status: 'analyzed' };
    if (filterClass.value) params.classification = filterClass.value;
    const res = await conversationAnalysisAPI.getReports(params);
    reports.value = res.data?.payload ?? [];
    meta.value    = res.data?.meta ?? { total: 0, pages: 1 };
  } catch (e) {
    error.value = e?.response?.data?.error || e.message || 'Erro ao carregar análises';
  } finally {
    loading.value = false;
  }
}

onMounted(() => { fetchSummary(); fetchReports(); });
watch([fromDate, toDate, filterClass], () => { page.value = 1; fetchSummary(); fetchReports(); });
watch(page, fetchReports);

// Reanalyze
const reanalyzing = ref(new Set());
async function reanalyze(report) {
  reanalyzing.value.add(report.id);
  try {
    await conversationAnalysisAPI.reanalyze(report.id);
    await fetchReports();
  } catch { /* ignore */ }
  finally { reanalyzing.value.delete(report.id); }
}

function openConversation(displayId) {
  const accountId = window.location.pathname.split('/')[3];
  window.open(`${window.location.origin}/app/accounts/${accountId}/conversations/${displayId}`, '_blank');
}

const summaryCards = computed(() => [
  { label: 'Total Analisado',  value: summary.value?.total ?? '—',    color: 'text-n-slate-12', bg: 'bg-n-alpha-2 border-n-weak', icon: 'i-lucide-file-search' },
  { label: 'Positivos',        value: summary.value?.positive ?? '—',  color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200', icon: 'i-lucide-thumbs-up' },
  { label: 'Neutros',          value: summary.value?.neutral ?? '—',   color: 'text-blue-600',    bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200',          icon: 'i-lucide-minus' },
  { label: 'Críticos',         value: summary.value?.critical ?? '—',  color: 'text-red-600',     bg: 'bg-red-50 dark:bg-red-900/20 border-red-200',            icon: 'i-lucide-alert-triangle' },
  { label: 'Objetivo Atingido', value: summary.value?.objective_met ?? '—', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200', icon: 'i-lucide-check-circle' },
  { label: 'Sentimento Médio', value: summary.value?.avg_sentiment != null ? summary.value.avg_sentiment.toFixed(2) : '—', color: 'text-n-slate-12', bg: 'bg-n-alpha-2 border-n-weak', icon: 'i-lucide-heart' },
]);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h1 class="text-xl font-bold text-n-slate-12">Análise de Conversas</h1>
        <p class="text-sm text-n-slate-10 mt-0.5">Relatório de qualidade gerado por IA para cada atendimento encerrado</p>
      </div>

      <div class="flex items-center gap-2 bg-white dark:bg-n-alpha-2 border border-n-weak rounded-lg px-3 py-2">
        <span class="i-lucide-calendar w-4 h-4 text-n-slate-10" />
        <input v-model="fromDate" type="date" class="text-sm bg-transparent text-n-slate-12 outline-none" />
        <span class="text-n-slate-9">–</span>
        <input v-model="toDate" type="date" class="text-sm bg-transparent text-n-slate-12 outline-none" />
      </div>
    </div>

    <!-- Summary KPIs -->
    <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <div
        v-for="card in summaryCards"
        :key="card.label"
        :class="['flex flex-col gap-2 p-3 rounded-xl border shadow-sm', card.bg]"
      >
        <div class="flex items-center justify-between">
          <p class="text-xs font-medium text-n-slate-10">{{ card.label }}</p>
          <span :class="[card.icon, 'w-3.5 h-3.5', card.color]" />
        </div>
        <p :class="['text-xl font-bold', card.color]">{{ card.value }}</p>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="flex gap-2 flex-wrap">
      <button
        v-for="opt in [
          { value: '', label: 'Todos' },
          { value: 'positive', label: 'Positivos' },
          { value: 'neutral',  label: 'Neutros' },
          { value: 'critical', label: 'Críticos' },
        ]"
        :key="opt.value"
        :class="[
          'px-3 py-1.5 text-xs rounded-full border transition-colors',
          filterClass === opt.value
            ? 'bg-n-brand text-white border-n-brand'
            : 'bg-white dark:bg-n-alpha-2 border-n-weak text-n-slate-11 hover:border-n-brand/40'
        ]"
        @click="filterClass = opt.value"
      >
        {{ opt.label }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-16 text-n-slate-9">
      <span class="i-lucide-loader-2 w-5 h-5 animate-spin mr-2" />
      Carregando análises...
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 text-red-700">
      <span class="i-lucide-alert-circle w-5 h-5 shrink-0" />
      <span class="text-sm">{{ error }}</span>
      <button class="ml-auto text-xs underline" @click="fetchReports">Tentar novamente</button>
    </div>

    <!-- Empty -->
    <div v-else-if="reports.length === 0" class="flex flex-col items-center justify-center py-16 gap-3 text-n-slate-9">
      <span class="i-lucide-file-search w-10 h-10 opacity-30" />
      <p class="text-sm">Nenhuma análise encontrada neste período.</p>
      <p class="text-xs text-center max-w-xs">
        Ative <strong>"Análise Automática de Conversas"</strong> nas configurações do agente (aba Operação → Registros & Análise).
      </p>
    </div>

    <!-- Report cards -->
    <template v-else>
      <div class="flex flex-col gap-3">
        <div
          v-for="report in reports"
          :key="report.id"
          :class="[
            'p-4 rounded-xl border shadow-sm bg-white dark:bg-n-alpha-2 transition-all',
            classInfo(report.classification).border
          ]"
        >
          <!-- Card header -->
          <div class="flex items-start justify-between gap-3 mb-3">
            <div class="flex items-center gap-2 flex-wrap">
              <!-- Classification badge -->
              <span :class="[
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
                classInfo(report.classification).bg,
                classInfo(report.classification).color,
                classInfo(report.classification).border
              ]">
                <span :class="[classInfo(report.classification).icon, 'w-3 h-3']" />
                {{ classInfo(report.classification).label }}
              </span>

              <!-- Objective badge -->
              <span
                v-if="report.objective_met !== null"
                :class="[
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border',
                  report.objective_met
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 border-emerald-200'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-700 border-red-200'
                ]"
              >
                <span :class="[report.objective_met ? 'i-lucide-check' : 'i-lucide-x', 'w-3 h-3']" />
                {{ report.objective_met ? 'Objetivo atingido' : 'Objetivo não atingido' }}
              </span>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              <p class="text-xs text-n-slate-9">{{ report.conversation?.contact?.name || 'Desconhecido' }}</p>
              <button
                class="flex items-center gap-1 text-xs text-n-brand hover:underline"
                @click="openConversation(report.conversation?.display_id)"
              >
                <span class="i-lucide-external-link w-3.5 h-3.5" />
                Ver
              </button>
              <button
                :disabled="reanalyzing.has(report.id)"
                class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-brand transition-colors disabled:opacity-40"
                @click="reanalyze(report)"
              >
                <span :class="['w-3.5 h-3.5', reanalyzing.has(report.id) ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw']" />
                Reanalisar
              </button>
            </div>
          </div>

          <!-- Summary -->
          <p class="text-sm text-n-slate-11 mb-3 leading-relaxed">{{ report.summary }}</p>

          <!-- Highlights + Issues -->
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div v-if="report.highlights?.length" class="flex flex-col gap-1.5">
              <p class="text-xs font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                <span class="i-lucide-star w-3 h-3" />
                Pontos Positivos
              </p>
              <ul class="space-y-1">
                <li
                  v-for="(h, i) in report.highlights"
                  :key="i"
                  class="flex items-start gap-1.5 text-xs text-n-slate-11"
                >
                  <span class="i-lucide-check w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />
                  {{ h }}
                </li>
              </ul>
            </div>

            <div v-if="report.issues?.length" class="flex flex-col gap-1.5">
              <p class="text-xs font-medium text-red-700 dark:text-red-400 flex items-center gap-1">
                <span class="i-lucide-alert-circle w-3 h-3" />
                Problemas / Reclamações
              </p>
              <ul class="space-y-1">
                <li
                  v-for="(issue, i) in report.issues"
                  :key="i"
                  class="flex items-start gap-1.5 text-xs text-n-slate-11"
                >
                  <span class="i-lucide-x w-3 h-3 text-red-500 shrink-0 mt-0.5" />
                  {{ issue }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between mt-3 pt-3 border-t border-n-weak">
            <div class="flex items-center gap-3 text-xs text-n-slate-9">
              <span v-if="report.conversation?.team">
                <span class="i-lucide-users w-3.5 h-3.5 inline mr-0.5" />
                {{ report.conversation.team.name }}
              </span>
              <span v-if="report.conversation?.assignee">
                <span class="i-lucide-user w-3.5 h-3.5 inline mr-0.5" />
                {{ report.conversation.assignee.name }}
              </span>
              <span v-if="report.agent">
                <span class="i-lucide-bot w-3.5 h-3.5 inline mr-0.5" />
                {{ report.agent.agent_name }}
              </span>
            </div>

            <!-- Sentiment bar -->
            <div v-if="report.sentiment_score !== null" class="flex items-center gap-2">
              <span class="text-xs text-n-slate-9">Sentimento</span>
              <div class="w-20 h-1.5 bg-n-alpha-3 rounded-full overflow-hidden">
                <div
                  class="h-1.5 rounded-full transition-all"
                  :class="report.sentiment_score >= 0.2 ? 'bg-emerald-400' : report.sentiment_score <= -0.2 ? 'bg-red-400' : 'bg-blue-400'"
                  :style="{ width: `${((report.sentiment_score + 1) / 2) * 100}%` }"
                />
              </div>
              <span class="text-xs text-n-slate-9">{{ report.sentiment_score?.toFixed(2) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-between text-xs text-n-slate-10">
        <span>{{ meta.total }} análises · Página {{ page }} de {{ meta.pages }}</span>
        <div class="flex gap-2">
          <button :disabled="page <= 1" class="px-3 py-1.5 rounded-lg border border-n-weak disabled:opacity-40 hover:bg-n-alpha-2" @click="page--">Anterior</button>
          <button :disabled="page >= meta.pages" class="px-3 py-1.5 rounded-lg border border-n-weak disabled:opacity-40 hover:bg-n-alpha-2" @click="page++">Próxima</button>
        </div>
      </div>
    </template>
  </div>
</template>
